import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { BANNER_API_BASE_URL, getApiHeaders } from '../config/api';

type Banner = {
  _id: string;
  imageUrl: string;
  navUrl: string;
  navType?: 'WEB' | 'SCREEN';
  priority: number;
  isActive: boolean;
  expiresAt?: string;
  createdAt: string;
};

type Section = {
  _id: string;
  sectionNumber: number;
  maxDisplay: number;
  banners: Banner[];
};

type ScreenBanners = {
  screen: string;
  sections: Section[];
};

const Banners: React.FC = () => {
  const navigate = useNavigate();
  const [screenBanners, setScreenBanners] = useState<ScreenBanners[]>([]);
  const [loading, setLoading] = useState(true);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('wallet_assets_user_id');
    
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    
    if (!userId) {
      toast.error('User ID not found. Please log in again.');
      return null;
    }
    
    const baseHeaders = getApiHeaders();
    const headers: Record<string, string> = {
      'x-api-key': baseHeaders['x-api-key'],
      'userid': userId,
      'accesstoken': token,
    };
    return headers;
  }, []);

  const fetchBanners = useCallback(async () => {
    const headers = headerWithAuth();
    if (!headers) return;

    setLoading(true);
    try {
      const res = await fetch(`${BANNER_API_BASE_URL}/banners`, {
        method: 'GET',
        headers,
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || 'Failed to fetch banners');
      }

      const data = json.data || [];
      setScreenBanners(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  }, [headerWithAuth]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleDeleteBanner = async (screen: string, sectionId: string, bannerId: string) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;

    const headers = headerWithAuth();
    if (!headers) return;

    try {
      const res = await fetch(
        `${BANNER_API_BASE_URL}/banners/screen/${screen}/section/${sectionId}/banner/${bannerId}`,
        {
          method: 'DELETE',
          headers,
        }
      );

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || 'Failed to delete banner');
      }

      toast.success('Banner deleted successfully');
      fetchBanners();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to delete banner');
    }
  };

  const handleDeleteSection = async (screen: string, sectionId: string) => {
    if (!window.confirm('Are you sure you want to delete this entire section and all its banners?')) return;

    const headers = headerWithAuth();
    if (!headers) return;

    try {
      const res = await fetch(
        `${BANNER_API_BASE_URL}/banners/screen/${screen}/section/${sectionId}`,
        {
          method: 'DELETE',
          headers,
        }
      );

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || 'Failed to delete section');
      }

      toast.success('Section deleted successfully');
      fetchBanners();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to delete section');
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>
        {/* Header */}
        <div className='flex items-center justify-between mb-8'>
          <div>
            <h1 className='text-4xl font-bold text-white mb-2'>Banner Management</h1>
            <p className='text-gray-400'>View and manage all promotional banners</p>
          </div>
          <button
            onClick={() => navigate('/banners/new')}
            className='px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-all shadow-lg'
          >
            + Create Banner
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className='flex items-center justify-center py-20'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-white'></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && screenBanners.length === 0 && (
          <div className='text-center py-20'>
            <svg className='mx-auto h-24 w-24 text-gray-500 mb-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z' />
            </svg>
            <h3 className='text-xl font-semibold text-gray-300 mb-2'>No banners yet</h3>
            <p className='text-gray-500 mb-6'>Get started by creating your first banner</p>
            <button
              onClick={() => navigate('/banners/new')}
              className='px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-all'
            >
              Create Banner
            </button>
          </div>
        )}

        {/* Banner Screens */}
        {!loading && screenBanners.length > 0 && (
          <div className='space-y-8'>
            {screenBanners.map((screenData) => (
              <div key={screenData.screen} className='bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl'>
                {/* Screen Header */}
                <div className='flex items-center justify-between mb-6'>
                  <div className='flex items-center gap-3'>
                    <div className='w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center'>
                      <svg className='w-7 h-7 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
                      </svg>
                    </div>
                    <div>
                      <h2 className='text-2xl font-bold text-white'>{screenData.screen}</h2>
                      <p className='text-sm text-gray-400'>{screenData.sections.length} section{screenData.sections.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>

                {/* Sections */}
                <div className='space-y-6'>
                  {screenData.sections.map((section) => (
                    <div key={section._id} className='bg-white/5 border border-white/10 rounded-xl p-5'>
                      {/* Section Header */}
                      <div className='flex items-center justify-between mb-4'>
                        <div 
                          className='flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity'
                          onClick={() => navigate(`/banners/${screenData.screen}/section/${section._id}`)}
                        >
                          <span className='px-3 py-1 rounded-lg text-sm font-semibold bg-purple-500/20 text-purple-300'>
                            Section {section.sectionNumber}
                          </span>
                          <span className='text-sm text-gray-400'>
                            {section.banners.length} banner{section.banners.length !== 1 ? 's' : ''} • Max display: {section.maxDisplay}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteSection(screenData.screen, section._id)}
                          className='px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm transition-all'
                        >
                          Delete Section
                        </button>
                      </div>

                      {/* Banners Grid */}
                      {section.banners.length > 0 ? (
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                          {section.banners.map((banner) => (
                            <div key={banner._id} className='bg-white/5 border border-white/10 rounded-lg overflow-hidden hover:border-purple-500/50 transition-all group'>
                              {/* Banner Image */}
                              <div className='relative aspect-video bg-gray-800'>
                                <img
                                  src={banner.imageUrl}
                                  alt='Banner'
                                  className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer'
                                  onClick={() => navigate(`/banners/${screenData.screen}/section/${section._id}/banner/${banner._id}`)}
                                />
                                <div className='absolute top-2 left-2 px-2 py-1 rounded bg-black/60 text-white text-xs font-semibold'>
                                  Priority {banner.priority}
                                </div>
                                {banner.expiresAt && (
                                  <div className='absolute top-2 right-2 px-2 py-1 rounded bg-orange-500/80 text-white text-xs font-semibold'>
                                    Expires {new Date(banner.expiresAt).toLocaleDateString()}
                                  </div>
                                )}
                              </div>

                              {/* Banner Info */}
                              <div className='p-4'>
                                <div className='flex items-start justify-between mb-2'>
                                  <div className='flex-1'>
                                    <p className='text-sm text-gray-400 mb-1'>Navigation:</p>
                                    <p className='text-white text-sm font-mono truncate'>{banner.navUrl}</p>
                                  </div>
                                  <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                                    banner.navType === 'WEB' ? 'bg-blue-500/20 text-blue-300' : 'bg-green-500/20 text-green-300'
                                  }`}>
                                    {banner.navType || 'WEB'}
                                  </span>
                                </div>

                                  <div className='flex items-center justify-between mt-3'>
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    banner.isActive ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {banner.isActive ? 'Active' : 'Inactive'}
                                  </span>
                                  <div className='flex items-center gap-2'>
                                    <button
                                      onClick={() => navigate(`/banners/${screenData.screen}/section/${section._id}/banner/${banner._id}`)}
                                      className='text-blue-400 hover:text-blue-300 transition-colors'
                                      title='Edit Banner'
                                    >
                                      <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteBanner(screenData.screen, section._id, banner._id)}
                                      className='text-red-400 hover:text-red-300 transition-colors'
                                      title='Delete Banner'
                                    >
                                      <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className='text-center py-8 text-gray-500'>
                          No banners in this section
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Banners;
